import { IconNetwork, IconRefresh } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Endpoints } from '@xver/apis';
import type { PrivateNetworkState } from '@xver/apis/v1/projects/model';
import { Button } from '@xver/components/Atoms';
import { useProjectFeedback } from '@xver/hooks/useProjectQueries';
import { focusAfterRender } from '@xver/utils/projects';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface PrivateNetworkAlertProps {
    instanceId: number;
    state: PrivateNetworkState | null | undefined;
    canManage: boolean;
}

/** Keep it mounted even when `state` is null: its live region must exist before a change. */
export default function PrivateNetworkAlert({
    instanceId,
    state,
    canManage,
}: PrivateNetworkAlertProps) {
    const { t } = useTranslation('projects');
    const queryClient = useQueryClient();
    const { errorMessage, notifySuccess } = useProjectFeedback();
    const containerRef = useRef<HTMLDivElement>(null);
    const [previousState, setPreviousState] = useState(state);
    const [connected, setConnected] = useState(false);
    if (state !== previousState) {
        setPreviousState(state);
        setConnected(!!previousState && !state);
    }

    const reapply = useMutation({
        mutationFn: () => Endpoints.v1.Instances.reapplyPrivateNetwork(instanceId),
        onSuccess: async () => {
            notifySuccess(t('privnet.reapplyStarted.title'), t('privnet.reapplyStarted.message'));
            await queryClient.invalidateQueries({ queryKey: ['api.v1.instances.get', instanceId] });
            focusAfterRender(() => containerRef.current);
        },
    });

    return (
        <div
            ref={containerRef}
            tabIndex={-1}
            className={
                state || connected
                    ? 'w-full mt-6 p-4 flex flex-row items-start gap-3 rounded-md border-[0.5px] border-line bg-surface-1'
                    : undefined
            }
        >
            {(state || connected) && (
                <IconNetwork size={22} className="shrink-0 text-fg/78" aria-hidden="true" />
            )}
            <div className="flex flex-col items-start gap-2 min-w-0">
                <div role="status" className="flex flex-col gap-1">
                    {!state && connected && (
                        <p className="text-base font-bold text-fg">{t('privnet.connected')}</p>
                    )}
                    {state && (
                        <>
                            <p className="text-base font-bold text-fg">
                                {t(`privnet.${state}.title`)}
                            </p>
                            <p className="max-w-[80ch] text-sm text-fg/78">
                                {t(`privnet.${state}.message`)}
                            </p>
                        </>
                    )}
                </div>
                {state && state !== 'pending' && canManage && (
                    <Button
                        size="sm"
                        variant="secondary"
                        leftIcon={<IconRefresh size={16} aria-hidden="true" />}
                        isLoading={reapply.isPending}
                        onClick={() => reapply.mutate()}
                    >
                        {t('privnet.reapply')}
                    </Button>
                )}
                {state && state !== 'pending' && !canManage && (
                    <p className="text-sm text-fg/68">{t('privnet.manageHint')}</p>
                )}
                {reapply.isError && (
                    <p role="alert" className="text-sm text-fg">
                        {errorMessage(reapply.error)}
                    </p>
                )}
            </div>
        </div>
    );
}
